/**
 * Date utility functions for formatting and manipulating dates
 */

/**
 * Format a date string or Date object to a human-readable format
 * @param {string|Date} dateString - The date to format
 * @param {string} format - Optional format (default: 'HH:MM:SS DD/MM/YYYY')
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString, format = 'HH:MM:SS DD/MM/YYYY') => {
  if (!dateString) return '';
  
  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    
    if (isNaN(date.getTime())) {
      console.warn('Invalid date:', dateString);
      return dateString;
    }
    
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    // Replace format tokens with actual values
    return format
      .replace('HH', hours)
      .replace('MM', minutes)
      .replace('SS', seconds)
      .replace('DD', day)
      .replace('MM', month)
      .replace('YYYY', year);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

/**
 * Format a date to ISO format (YYYY-MM-DD)
 * @param {Date} date - The date to format
 * @returns {string} ISO formatted date
 */
export const formatToISODate = (date) => {
  if (!date) return '';
  
  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toISOString().split('T')[0];
  } catch (error) {
    console.error('Error formatting to ISO date:', error);
    return '';
  }
};

/**
 * Get the current date in ISO format
 * @returns {string} Current date in ISO format
 */
export const getCurrentISODate = () => {
  return new Date().toISOString().split('T')[0];
};

/**
 * Calculate the difference between two dates in days
 * @param {Date|string} date1 - First date
 * @param {Date|string} date2 - Second date
 * @returns {number} Difference in days
 */
export const daysBetween = (date1, date2) => {
  const d1 = typeof date1 === 'string' ? new Date(date1) : date1;
  const d2 = typeof date2 === 'string' ? new Date(date2) : date2;
  
  // Convert to UTC to avoid timezone issues
  const utc1 = Date.UTC(d1.getFullYear(), d1.getMonth(), d1.getDate());
  const utc2 = Date.UTC(d2.getFullYear(), d2.getMonth(), d2.getDate());
  
  // Calculate difference in milliseconds and convert to days
  return Math.floor((utc2 - utc1) / (1000 * 60 * 60 * 24));
};
